/**
 * FERP Portal - Briefing Template List Page
 *
 * Config type ast-brief-: Assistant briefing templates define PHI-free operational
 * briefings triggered by workflow events (DRR-131). Templates are YAML configs
 * evaluated by EmitAssistantBriefing workflow action → OutboxProcessor → LLM.
 *
 * CONSTRAINTS:
 * - Mantine v8.x only
 * - Dumb Frontend: displays backend-provided template metadata
 * - Admin view layer only
 */

import { useBriefingTemplates } from '@/hooks/useBriefingTemplates';
import type { BriefingTemplate } from '@/hooks/useBriefingTemplates';
import {
  Alert,
  Anchor,
  Badge,
  Card,
  Container,
  Group,
  Loader,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from '@mantine/core';
import {
  IconBell,
  IconInfoCircle,
  IconMessageChatbot,
  IconShieldCheck,
  IconUrgent,
} from '@tabler/icons-react';

const URGENCY_MAP: Record<string, string> = {
  day_boundary: 'normal',
  authorization_expiring: 'high',
  surveyor_arrived: 'critical',
};

const URGENCY_COLORS: Record<string, string> = {
  normal: 'blue',
  high: 'orange',
  critical: 'red',
};

function getUrgency(triggerEvent: string): string {
  return URGENCY_MAP[triggerEvent] ?? 'normal';
}

export function BriefingTemplateListPage() {
  const { templates, loading, error } = useBriefingTemplates();

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        <Group justify="space-between">
          <div>
            <Title order={2}>Briefing Templates</Title>
            <Text c="dimmed" size="sm">
              Config type ast-brief- — PHI-free operational briefings triggered by workflow events
              (DRR-131)
            </Text>
          </div>
          <Badge variant="light" color="violet" size="lg">
            {templates.length} Templates
          </Badge>
        </Group>

        {error && (
          <Alert icon={<IconInfoCircle size={16} />} color="orange" title="Query Unavailable">
            Could not load briefing templates from backend. Ensure assistant:briefing:view scope is
            granted.
          </Alert>
        )}

        {loading && (
          <Group justify="center" py="xl">
            <Loader size="sm" />
            <Text size="sm" c="dimmed">
              Loading briefing templates...
            </Text>
          </Group>
        )}

        <Card withBorder p="md">
          <Group gap="sm" mb="xs">
            <ThemeIcon variant="light" color="violet">
              <IconMessageChatbot size={18} />
            </ThemeIcon>
            <Text fw={600}>How Briefings Work</Text>
          </Group>
          <Text size="sm" c="dimmed">
            Workflow state entry →{' '}
            <Text span ff="monospace" size="sm">
              EmitAssistantBriefing
            </Text>{' '}
            action →{' '}
            <Text span ff="monospace" size="sm">
              OutboxProcessor
            </Text>{' '}
            → LLM renders template with PHI-free context variables → Dashboard notification + SMS
            (if escalation threshold met)
          </Text>
          <Group gap="xs" mt="xs">
            <Badge variant="outline" size="xs" color="green">
              <IconShieldCheck size={10} /> PHI-Free
            </Badge>
            <Text size="xs" c="dimmed">
              Context builder blocks all patient names, DOBs, staff IDs, T3/T4 data
            </Text>
          </Group>
        </Card>

        <Stack gap="sm">
          {templates.map((template) => {
            const urgency = getUrgency(template.triggerEvent);
            return (
              <Card key={template.briefingId} withBorder p="md">
                <Group justify="space-between" mb="sm">
                  <Group gap="sm">
                    <ThemeIcon
                      variant="light"
                      color={URGENCY_COLORS[urgency] ?? 'gray'}
                      size="sm"
                    >
                      {urgency === 'critical' ? (
                        <IconUrgent size={14} />
                      ) : (
                        <IconBell size={14} />
                      )}
                    </ThemeIcon>
                    <div>
                      <Text fw={500}>{template.displayName}</Text>
                      <Text size="xs" c="dimmed" ff="monospace">
                        {template.briefingId}
                      </Text>
                    </div>
                  </Group>
                  <Badge
                    variant="light"
                    color={URGENCY_COLORS[urgency] ?? 'gray'}
                    size="sm"
                  >
                    {urgency}
                  </Badge>
                </Group>

                <Text size="sm" c="dimmed" mb="sm">
                  {template.description}
                </Text>

                <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="sm">
                  <div>
                    <Text size="xs" c="dimmed">
                      Trigger Event
                    </Text>
                    <Badge variant="outline" size="sm" ff="monospace">
                      {template.triggerEvent}
                    </Badge>
                  </div>
                  <div>
                    <Text size="xs" c="dimmed">
                      Delivery
                    </Text>
                    <Group gap={4}>
                      {template.deliveryChannels.map((d) => (
                        <Badge key={d} variant="outline" size="xs">
                          {d}
                        </Badge>
                      ))}
                    </Group>
                  </div>
                  <div>
                    <Text size="xs" c="dimmed">
                      Sections / Context Vars
                    </Text>
                    <Text size="sm">
                      {template.sectionCount} sections, {template.contextVariableCount} variables
                    </Text>
                  </div>
                </SimpleGrid>
              </Card>
            );
          })}
        </Stack>

        <Text size="xs" c="dimmed">
          To add or modify briefing templates, edit{' '}
          <Anchor size="xs" href="#" onClick={(e) => e.preventDefault()}>
            common/configs/seed/briefing-templates/*.yaml
          </Anchor>{' '}
          and re-seed (Rust ferp_seed binary).
        </Text>
      </Stack>
    </Container>
  );
}

export default BriefingTemplateListPage;
